
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "graphql-error-responses"
    }}>{`GraphQL error responses`}</h1>
    <p>{`Unlike REST APIs, GraphQL servers don’t use HTTP status codes to indicate unsuccessful requests.
Instead, the GraphQL response body includes an array of `}<inlineCode parentName="p">{`errors`}</inlineCode>{` when an operation fails.`}</p>
    <p>{`The SEEK API uses four error codes to classify the type of error that occurred.
Depending on the type of error there will be further information in the error’s `}<inlineCode parentName="p">{`message`}</inlineCode>{` and `}<inlineCode parentName="p">{`extensions`}</inlineCode>{`.`}</p>
    <p>{`The SEEK API’s error messages are safe to expose to SEEK hirers.
However, some errors responses can indicate a problem with your SEEK API integration that’s not actionable by an end user.
If you decide to hide those error messages, your software should still log the full message to support debugging issues.`}</p>
    <h2 {...{
      "id": "unauthenticated"
    }}>{`UNAUTHENTICATED`}</h2>
    <p>{`An `}<inlineCode parentName="p">{`UNAUTHENTICATED`}</inlineCode>{` error indicates a problem with the `}<a parentName="p" {...{
        "href": "/auth"
      }}>{`access token`}</a>{` included in the request.
More details will be available in the error’s `}<inlineCode parentName="p">{`message`}</inlineCode>{`.`}</p>
    <h3 {...{
      "id": "common-causes"
    }}>{`Common causes`}</h3>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`An access token wasn’t included in the request’s `}<inlineCode parentName="p">{`Authorization`}</inlineCode>{` header.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The access token has expired.
This can happen if the access token is used after the `}<inlineCode parentName="p">{`expires_in`}</inlineCode>{` from the token response.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`A `}<a parentName="p" {...{
            "href": "/graphql/playground"
          }}>{`Playground`}</a>{` access token was used to access live data or vice versa.`}</p>
      </li>
    </ul>
    <h3 {...{
      "id": "example-response"
    }}>{`Example response`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "errors": [
    {
      "message": "Public test partner token is expired",
      "locations": [
        {
          "line": 2,
          "column": 3
        }
      ],
      "path": ["candidateProfile"],
      "extensions": {
        "code": "UNAUTHENTICATED"
      }
    }
  ]
}
`}</code></pre>
    <h3 {...{
      "id": "retrying-authentication-errors"
    }}>{`Retrying authentication errors`}</h3>
    <p>{`If you receive an `}<inlineCode parentName="p">{`UNAUTHENTICATED`}</inlineCode>{` error due to an expired access token as per the example above,
your software may request a new `}<a parentName="p" {...{
        "href": "/auth#partner-and-browser-tokens"
      }}>{`access token`}</a>{` and use it to automatically retry the request.
Limit such behaviour to a single retry;
if your software continues to receive the same error with a newly issued token then you have likely misconfigured the request and further retries will not help.`}</p>
    <p>{`You may be able to customise your GraphQL or HTTP `}<a parentName="p" {...{
        "href": "/graphql/in-practice#client-libraries-and-tools"
      }}>{`client library`}</a>{` to implement a retry policy.
Ensure that your retry policy takes the error code in the response into account,
as retrying a request with a different error like `}<a parentName="p" {...{
        "href": "#forbidden"
      }}><inlineCode parentName="a">{`FORBIDDEN`}</inlineCode></a>{` is unlikely to produce a different result.`}</p>
    <h2 {...{
      "id": "forbidden"
    }}>{`FORBIDDEN`}</h2>
    <p>{`A `}<inlineCode parentName="p">{`FORBIDDEN`}</inlineCode>{` error indicates a valid access token was received but there was a problem authorizing the operation.
More details will be available in the error’s `}<inlineCode parentName="p">{`message`}</inlineCode>{`.`}</p>
    <p>{`It’s possible to have access to an object but not all of its nested objects or fields.
For example, you can query a `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/PositionProfile"
      }}><inlineCode parentName="a">{`PositionProfile`}</inlineCode></a>{` for Optimised Apply but wouldn’t be able to query its `}<inlineCode parentName="p">{`branding`}</inlineCode>{` without an additional `}<inlineCode parentName="p">{`JobPosting`}</inlineCode>{` relationship.
To help debugging these situations the error’s `}<inlineCode parentName="p">{`path`}</inlineCode>{` will indicate which field encountered the `}<inlineCode parentName="p">{`FORBIDDEN`}</inlineCode>{` error.`}</p>
    <p>{`Requests that fail authorization should not be automatically retried.`}</p>
    <h3 {...{
      "id": "common-causes-1"
    }}>{`Common causes`}</h3>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`SEEK has not `}<a parentName="p" {...{
            "href": "/auth/hirer-relationships"
          }}>{`configured a relationship`}</a>{` between you and the SEEK hirer.`}</p>
        <p parentName="li">{`You can view a list of your SEEK hirers and their relationships on the `}<a parentName="p" {...{
            "href": "/manage/hirers"
          }}>{`Developer Dashboard’s hirers page`}</a>{`.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The SEEK hirer’s account has been disabled.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`A `}<a parentName="p" {...{
            "href": "/auth/browser-tokens"
          }}>{`browser token`}</a>{` was used without the scope required for the operation.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`You used a mutation against `}<a parentName="p" {...{
            "href": "/graphql/playground"
          }}>{`Playground`}</a>{` data.`}</p>
        <p parentName="li">{`The Playground returns `}<inlineCode parentName="p">{`FORBIDDEN`}</inlineCode>{` errors after `}<a parentName="p" {...{
            "href": "#bad_user_input"
          }}>{`validating the mutation’s input`}</a>{`.
You can use this feature to test input validation in the Playground environment.`}</p>
      </li>
    </ul>
    <h3 {...{
      "id": "example-failing-query"
    }}>{`Example failing query`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($id: String!) {\\n  # Hirer has been disabled\\n  hiringOrganization(id: $id) {\\n    name\\n  }\\n}\",\"position\":{\"start\":{\"line\":90,\"column\":1,\"offset\":3616},\"end\":{\"line\":97,\"column\":4,\"offset\":3728},\"indent\":[1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"id\\\": \\\"seekAnzPublicTest:organization:seek:EXDtpepX\\\"\\n}\",\"position\":{\"start\":{\"line\":99,\"column\":1,\"offset\":3730},\"end\":{\"line\":103,\"column\":4,\"offset\":3800},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"errors\\\": [\\n    {\\n      \\\"message\\\": \\\"Partner does not have a relationship with the hirer\\\",\\n      \\\"locations\\\": [\\n        {\\n          \\\"line\\\": 2,\\n          \\\"column\\\": 3\\n        }\\n      ],\\n      \\\"path\\\": [\\\"hiringOrganization\\\"],\\n      \\\"extensions\\\": {\\n        \\\"code\\\": \\\"FORBIDDEN\\\"\\n      }\\n    }\\n  ]\\n}\",\"position\":{\"start\":{\"line\":105,\"column\":1,\"offset\":3802},\"end\":{\"line\":123,\"column\":4,\"offset\":4108},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($id:": true,
        "String!)": true,
        "{\\n": true,
        "": true,
        "#": true,
        "Hirer": true,
        "has": true,
        "been": true,
        "disabled\\n": true,
        "hiringOrganization(id:": true,
        "$id)": true,
        "name\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":90,\"column\":1,\"offset\":3616},\"end\":{\"line\":97,\"column\":4,\"offset\":3728},\"indent\":[1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"id\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:EXDtpepX\\\"\\n}\",\"position\":{\"start\":{\"line\":99,\"column\":1,\"offset\":3730},\"end\":{\"line\":103,\"column\":4,\"offset\":3800},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"errors\\\":": true,
        "[\\n": true,
        "\\\"message\\\":": true,
        "\\\"Partner": true,
        "does": true,
        "not": true,
        "have": true,
        "a": true,
        "relationship": true,
        "with": true,
        "the": true,
        "hirer\\\",\\n": true,
        "\\\"locations\\\":": true,
        "\\\"line\\\":": true,
        "2,\\n": true,
        "\\\"column\\\":": true,
        "3\\n": true,
        "}\\n": true,
        "],\\n": true,
        "\\\"path\\\":": true,
        "[\\\"hiringOrganization\\\"],\\n": true,
        "\\\"extensions\\\":": true,
        "\\\"code\\\":": true,
        "\\\"FORBIDDEN\\\"\\n": true,
        "]\\n}\",\"position\":{\"start\":{\"line\":105,\"column\":1,\"offset\":3802},\"end\":{\"line\":123,\"column\":4,\"offset\":4108},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($id: String!) {
  # Hirer has been disabled
  hiringOrganization(id: $id) {
    name
  }
}
`}</code></pre>
    <h2 {...{
      "id": "bad_user_input"
    }}>{`BAD_USER_INPUT`}</h2>
    <p>{`A `}<inlineCode parentName="p">{`BAD_USER_INPUT`}</inlineCode>{` error indicates the operation failed input validation.
A top-level summary of the validation failure will be available in the error’s `}<inlineCode parentName="p">{`message`}</inlineCode>{`.`}</p>
    <p>{`Some operations support additional field-level messages in the `}<inlineCode parentName="p">{`invalidFields`}</inlineCode>{` map.
Its keys are `}<a parentName="p" {...{
        "href": "https://datatracker.ietf.org/doc/html/rfc6901"
      }}>{`JSON Pointers`}</a>{` to the invalid fields in the operation’s arguments.
These map to a message describing why the field failed validation.`}</p>
    <p>{`Requests that fail validation should not be automatically retried.`}</p>
    <h3 {...{
      "id": "common-causes-2"
    }}>{`Common causes`}</h3>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`The input was the correct type for the GraphQL schema but violated a more specific constraint.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`A mutation’s input referenced an object that doesn’t exist.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The operation exceeded the SEEK API’s query complexity limit.`}</p>
        <p parentName="li">{`An operation’s complexity is calculated based on the estimated size of its response.
Overly complex operations are rejected before they’re executed to prevent `}<a parentName="p" {...{
            "href": "https://en.wikipedia.org/wiki/Denial-of-service_attack"
          }}>{`denial of service attacks`}</a>{`.`}</p>
      </li>
    </ul>
    <h3 {...{
      "id": "example-failing-mutation"
    }}>{`Example failing mutation`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation ($input: CreateWebhookSubscriptionInput!) {\\n  createWebhookSubscription(input: $input) {\\n    ... on CreateWebhookSubscriptionPayload_Success {\\n      webhookSubscription {\\n        id {\\n          value\\n        }\\n      }\\n    }\\n\\n    ... on CreateWebhookSubscriptionPayload_Conflict {\\n      conflictingWebhookSubscription {\\n        id {\\n          value\\n        }\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":149,\"column\":1,\"offset\":5063},\"end\":{\"line\":169,\"column\":4,\"offset\":5464},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    \\\"webhookSubscription\\\": {\\n      \\\"url\\\": \\\"https://localhost\\\",\\n      \\\"eventTypeCode\\\": \\\"PositionProfilePosted\\\",\\n      \\\"schemeId\\\": \\\"seekAnzPublicTest\\\",\\n      \\\"signingAlgorithmCode\\\": \\\"None\\\"\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":171,\"column\":1,\"offset\":5466},\"end\":{\"line\":182,\"column\":4,\"offset\":5691},\"indent\":[1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"errors\\\": [\\n    {\\n      \\\"message\\\": \\\"/input/webhookSubscription/url: Private hostnames are not accepted\\\",\\n      \\\"locations\\\": [\\n        {\\n          \\\"line\\\": 2,\\n          \\\"column\\\": 3\\n        }\\n      ],\\n      \\\"path\\\": [\\\"createWebhookSubscription\\\"],\\n      \\\"extensions\\\": {\\n        \\\"code\\\": \\\"BAD_USER_INPUT\\\",\\n        \\\"invalidFields\\\": {\\n          \\\"/input/webhookSubscription/url\\\": \\\"Private hostnames are not accepted\\\"\\n        }\\n      }\\n    }\\n  ]\\n}\",\"position\":{\"start\":{\"line\":184,\"column\":1,\"offset\":5693},\"end\":{\"line\":205,\"column\":4,\"offset\":6145},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation": true,
        "($input:": true,
        "CreateWebhookSubscriptionInput!)": true,
        "{\\n": true,
        "": true,
        "createWebhookSubscription(input:": true,
        "$input)": true,
        "...": true,
        "on": true,
        "CreateWebhookSubscriptionPayload_Success": true,
        "webhookSubscription": true,
        "id": true,
        "value\\n": true,
        "}\\n": true,
        "}\\n\\n": true,
        "CreateWebhookSubscriptionPayload_Conflict": true,
        "conflictingWebhookSubscription": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":149,\"column\":1,\"offset\":5063},\"end\":{\"line\":169,\"column\":4,\"offset\":5464},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "\\\"webhookSubscription\\\":": true,
        "\\\"url\\\":": true,
        "\\\"https://localhost\\\",\\n": true,
        "\\\"eventTypeCode\\\":": true,
        "\\\"PositionProfilePosted\\\",\\n": true,
        "\\\"schemeId\\\":": true,
        "\\\"seekAnzPublicTest\\\",\\n": true,
        "\\\"signingAlgorithmCode\\\":": true,
        "\\\"None\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":171,\"column\":1,\"offset\":5466},\"end\":{\"line\":182,\"column\":4,\"offset\":5691},\"indent\":[1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"errors\\\":": true,
        "[\\n": true,
        "\\\"message\\\":": true,
        "\\\"/input/webhookSubscription/url:": true,
        "Private": true,
        "hostnames": true,
        "are": true,
        "not": true,
        "accepted\\\",\\n": true,
        "\\\"locations\\\":": true,
        "\\\"line\\\":": true,
        "2,\\n": true,
        "\\\"column\\\":": true,
        "3\\n": true,
        "],\\n": true,
        "\\\"path\\\":": true,
        "[\\\"createWebhookSubscription\\\"],\\n": true,
        "\\\"extensions\\\":": true,
        "\\\"code\\\":": true,
        "\\\"BAD_USER_INPUT\\\",\\n": true,
        "\\\"invalidFields\\\":": true,
        "\\\"/input/webhookSubscription/url\\\":": true,
        "\\\"Private": true,
        "accepted\\\"\\n": true,
        "]\\n}\",\"position\":{\"start\":{\"line\":184,\"column\":1,\"offset\":5693},\"end\":{\"line\":205,\"column\":4,\"offset\":6145},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: CreateWebhookSubscriptionInput!) {
  createWebhookSubscription(input: $input) {
    ... on CreateWebhookSubscriptionPayload_Success {
      webhookSubscription {
        id {
          value
        }
      }
    }

    ... on CreateWebhookSubscriptionPayload_Conflict {
      conflictingWebhookSubscription {
        id {
          value
        }
      }
    }
  }
}
`}</code></pre>
    <h2 {...{
      "id": "internal_server_error"
    }}>{`INTERNAL_SERVER_ERROR`}</h2>
    <p>{`An `}<inlineCode parentName="p">{`INTERNAL_SERVER_ERROR`}</inlineCode>{` indicates an unexpected failure occurred while processing the request.`}</p>
    <p>{`Error details are omitted from the response to avoid exposing sensitive information about SEEK’s systems.
However, the message will contain a reference code SEEK can use to find more details in our internal logs.`}</p>
    <h3 {...{
      "id": "example-response-1"
    }}>{`Example response`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "errors": [
    {
      "message": "Oops. Something went wrong. If you continue to experience issues, contact support and quote error reference '112c-4edf-af8e'.",
      "path": ["positionProfile"],
      "extensions": {
        "code": "INTERNAL_SERVER_ERROR"
      }
    }
  ]
}
`}</code></pre>
    <h3 {...{
      "id": "retrying-server-errors"
    }}>{`Retrying server errors`}</h3>
    <p>{`Your software should have a reasonable retry policy on `}<inlineCode parentName="p">{`INTERNAL_SERVER_ERROR`}</inlineCode>{` errors for `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Robustness_(computer_science)"
      }}>{`robustness`}</a>{`.
This increases the likelihood that your integration will remain operational through transient failures in the SEEK API.
You may also time out and retry a request if it has taken too long for the SEEK API to respond.`}</p>
    <p>{`Requests that carry a `}<a parentName="p" {...{
        "href": "/graphql/in-practice#operations"
      }}>{`mutation`}</a>{` must be `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Idempotence"
      }}>{`idempotent`}</a>{` to prevent retries from unintentionally creating or modifying multiple objects.
For example, you must use the same `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/CreatePostingInstructionInput/field/idempotencyId"
      }}><inlineCode parentName="a">{`idempotencyId`}</inlineCode></a>{` when `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/managing-job-ads/posting-a-job-ad#idempotency"
      }}>{`retrying a job posting mutation`}</a>{` to ensure that only one job ad is created.`}</p>
    <p>{`You may be able to customise your GraphQL or HTTP `}<a parentName="p" {...{
        "href": "/graphql/in-practice#client-libraries-and-tools"
      }}>{`client library`}</a>{` to implement a retry policy.
Ensure that your retry policy takes the error code in the response into account,
as retrying a request with a different error like `}<a parentName="p" {...{
        "href": "#forbidden"
      }}><inlineCode parentName="a">{`FORBIDDEN`}</inlineCode></a>{` is unlikely to produce a different result.`}</p>
    <p>{`The policy behaviour may differ based on the use case.
A shorter request timeout and backoff may be appropriate a high-volume interactive hirer experience like `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/locations#option-1-build-a-seek-specific-location-input"
      }}>{`location suggest`}</a>{`.
On the other hand,
a back-end process like `}<a parentName="p" {...{
        "href": "/use-cases/application-export"
      }}>{`exporting applications`}</a>{` could have a longer delay between retries and send persistent failures to a `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Dead_letter_queue"
      }}>{`dead letter queue`}</a>{` for manual reprocessing once the SEEK API has restored service.`}</p>
    <p>{`Your software should cap the number of request retries and implement `}<a parentName="p" {...{
        "href": "https://aws.amazon.com/blogs/architecture/exponential-backoff-and-jitter/"
      }}>{`exponential backoff and jitter`}</a>{` as best practices.
By progressively increasing the delay between retries and introducing a random factor to the calculation,
you space out your requests and avoid overwhelming the SEEK API, increasing the likelihood that your requests will be served sooner.
Generally speaking, you should not retry a request more than 3 times.`}</p>
    <p>{`Contact SEEK if you need to discuss the specifics of your retry policy.`}</p>
    <h2 {...{
      "id": "other-errors"
    }}>{`Other errors`}</h2>
    <p>{`The GraphQL error codes above accompany a HTTP status code of `}<inlineCode parentName="p">{`200`}</inlineCode>{`.
However, the SEEK API may return HTTP status codes other than `}<inlineCode parentName="p">{`200`}</inlineCode>{` in some scenarios.`}</p>
    <p>{`If you encounter any of these and need assistance, `}<a parentName="p" {...{
        "href": "https://talent.seek.com.au/contactus"
      }}>{`contact SEEK’s support team`}</a>{` with details such as your `}<a parentName="p" {...{
        "href": "/graphql/in-practice#tracing-requests"
      }}>{`tracing headers`}</a>{`, the request body, as well as the status code, headers and body of the response.`}</p>
    <p>{`HTTP response status codes other than `}<inlineCode parentName="p">{`200`}</inlineCode>{` may include:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`400`}</inlineCode>{`, alongside a `}<inlineCode parentName="p">{`GRAPHQL_PARSE_FAILED`}</inlineCode>{` or `}<inlineCode parentName="p">{`GRAPHQL_VALIDATION_FAILED`}</inlineCode>{` GraphQL error codes.
These error codes indicate a problem with GraphQL syntax or schema usage with your software, and generally should only occur during development.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`400`}</inlineCode>{` or `}<inlineCode parentName="p">{`403`}</inlineCode>{` codes may be returned if security controls are triggered.
In this scenario, the response body might not be JSON.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`500`}</inlineCode>{`, `}<inlineCode parentName="p">{`502`}</inlineCode>{`, `}<inlineCode parentName="p">{`503`}</inlineCode>{` and other `}<inlineCode parentName="p">{`5xx`}</inlineCode>{` codes may indicate the SEEK API is experiencing an outage or other technical issue.
You can check the `}<a parentName="p" {...{
            "href": "https://status.seek.com/"
          }}>{`status page`}</a>{` for any known issues.
In this scenario, the response body might not be JSON.
Your software should implement a `}<a parentName="p" {...{
            "href": "#retrying-server-errors"
          }}>{`retry policy`}</a>{` for these errors.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Networking errors may surface in different forms depending on your technology stack.
For example, connection timeouts, socket timeouts, socket hangups, or otherwise.
This could indicate a problem in the SEEK API, your networking, or anything in between.
You can check the `}<a parentName="p" {...{
            "href": "https://status.seek.com/"
          }}>{`status page`}</a>{` for any known issues.
Your software should implement a `}<a parentName="p" {...{
            "href": "#retrying-server-errors"
          }}>{`retry policy`}</a>{` for these errors.`}</p>
      </li>
    </ul>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;